import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, formatEUR } from "@/components/app/Shared";
import { Plus, Trash2, X, Upload, Image as ImageIcon, Pencil, ArrowDownAZ, ArrowUpAZ, Search } from "lucide-react";
import { toast } from "sonner";

export const PRODUCT_CATEGORIES = ["Verse Groente", "Vlees, Vis & Diepvries", "Drinks", "Overige Food", "Non-Food"];

// Subcategories per parent (only Drinks and Non-Food have subs today)
export const PRODUCT_SUBCATEGORIES = {
  Drinks: ["Soft Drinks", "Water", "Alcohol"],
  "Non-Food": ["Hygiëne", "Take-Away", "Overig"],
};

// Display order in grouped table
const CATEGORY_ORDER = ["Verse Groente", "Vlees, Vis & Diepvries", "Overige Food", "Drinks", "Non-Food"];

const emptyForm = { name: "", unit: "stuk", price: 0, supplier_id: "", category: "Verse Groente", subcategory: "", image_url: "", barcode: "" };

export default function Products() {
  const { user } = useAuth();
  const isAdmin = ["admin", "organization_admin"].includes(user?.role);
  const canAdd = !!user;
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("alle");
  const [sortDir, setSortDir] = useState("asc"); // 'asc' | 'desc' | 'none'

  const load = () => Promise.all([api.get("/products"), api.get("/suppliers")]).then(([p, s]) => {
    setProducts(p.data); setSuppliers(s.data);
  });
  useEffect(() => { load(); }, []);

  const handleFile = (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Foto te groot (max 3 MB)"); return; }
    const reader = new FileReader();
    reader.onload = (e) => setForm((f) => ({ ...f, image_url: e.target.result }));
    reader.readAsDataURL(file);
  };

  const openNew = () => {
    setEditingId(null);
    setForm({ ...emptyForm, supplier_id: suppliers[0]?.id || "" });
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    const subs = PRODUCT_SUBCATEGORIES[p.category] || [];
    setForm({
      name: p.name || "",
      unit: p.unit || "stuk",
      price: p.price ?? 0,
      supplier_id: p.supplier_id || "",
      category: PRODUCT_CATEGORIES.includes(p.category) ? p.category : "Verse Groente",
      subcategory: subs.includes(p.subcategory) ? p.subcategory : "",
      image_url: p.image_url || "",
      barcode: p.barcode || "",
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const subs = PRODUCT_SUBCATEGORIES[form.category] || [];
    const payload = {
      ...form,
      price: parseFloat(form.price),
      barcode: form.barcode || null,
      supplier_id: form.supplier_id || null,
      subcategory: subs.length ? form.subcategory : null,
    };
    try {
      if (editingId) {
        await api.patch(`/products/${editingId}`, payload);
        toast.success("Product bijgewerkt");
      } else {
        await api.post("/products", payload);
        toast.success("Product toegevoegd");
      }
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm);
      load();
    } catch {
      toast.error("Opslaan mislukt");
    }
  };

  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const del = async (id) => {
    if (confirmDeleteId !== id) {
      setConfirmDeleteId(id);
      setTimeout(() => setConfirmDeleteId((curr) => (curr === id ? null : curr)), 3500);
      return;
    }
    try {
      await api.delete(`/products/${id}`);
      toast.success("Product verwijderd");
      setConfirmDeleteId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="products-page">
      <PageHeader
        overline="Catalogus"
        title="Producten"
        description="Beheer alle producten — inclusief foto's, categorieën en barcodes."
        actions={canAdd && (
          <button onClick={openNew} className="inline-flex items-center gap-2 h-10 px-4 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded-md text-sm font-semibold" data-testid="products-add-button">
            <Plus size={16} /> Nieuw product
          </button>
        )}
      />

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Zoeken op product…"
            className="w-full h-10 pl-9 pr-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#5BC044]/45 focus:border-[#5BC044] bg-white"
            data-testid="products-search"
          />
        </div>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
          className="h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#5BC044]/45"
          data-testid="products-category-filter"
        >
          <option value="alle">Alle categorieën</option>
          {PRODUCT_CATEGORIES.map((c) => {
            const subs = PRODUCT_SUBCATEGORIES[c];
            if (!subs) return <option key={c} value={c}>{c}</option>;
            return (
              <optgroup key={c} label={c}>
                <option value={c}>{c} — alle</option>
                {subs.map((s) => <option key={`${c}::${s}`} value={`${c}::${s}`}>{c} › {s}</option>)}
              </optgroup>
            );
          })}
        </select>
        <button
          onClick={() => setSortDir((d) => d === "asc" ? "desc" : "asc")}
          className="h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white hover:border-[#5BC044] hover:text-[#5BC044] inline-flex items-center gap-2"
          data-testid="products-sort-button"
          title={sortDir === "asc" ? "Sorteer Z-A" : "Sorteer A-Z"}
        >
          {sortDir === "asc" ? <ArrowDownAZ size={15} /> : <ArrowUpAZ size={15} />}
          {sortDir === "asc" ? "A-Z" : "Z-A"}
        </button>
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left">
              <th className="px-4 py-3 overline">Foto</th>
              <th className="px-4 py-3 overline">Product</th>
              <th className="px-4 py-3 overline">Barcode</th>
              <th className="px-4 py-3 overline">Categorie</th>
              <th className="px-4 py-3 overline">Leverancier</th>
              <th className="px-4 py-3 overline">Eenheid</th>
              <th className="px-4 py-3 overline text-right">Prijs</th>
              {isAdmin && <th className="px-4 py-3"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {(() => {
              let rows = [...products];
              if (search) {
                const q = search.toLowerCase();
                rows = rows.filter((p) => (p.name || "").toLowerCase().includes(q) || (p.barcode || "").includes(search));
              }
              if (categoryFilter !== "alle") {
                if (categoryFilter.includes("::")) {
                  const [cat, sub] = categoryFilter.split("::");
                  rows = rows.filter((p) => p.category === cat && p.subcategory === sub);
                } else {
                  rows = rows.filter((p) => p.category === categoryFilter);
                }
              }
              rows.sort((a, b) => {
                const r = (a.name || "").localeCompare(b.name || "", "nl", { sensitivity: "base" });
                return sortDir === "asc" ? r : -r;
              });

              // Group by category > subcategory for easier scanning
              const groups = new Map(); // key: `${cat}` or `${cat}::${sub}` -> {label, rows[]}
              for (const p of rows) {
                const cat = p.category || "Overig";
                const subs = PRODUCT_SUBCATEGORIES[cat];
                const sub = subs && subs.includes(p.subcategory) ? p.subcategory : null;
                const key = sub ? `${cat}::${sub}` : cat;
                const label = sub ? `${cat} › ${sub}` : cat;
                if (!groups.has(key)) groups.set(key, { label, cat, sub, rows: [] });
                groups.get(key).rows.push(p);
              }

              // Order groups: by CATEGORY_ORDER, then by subcategory order from PRODUCT_SUBCATEGORIES
              const sortedKeys = [...groups.keys()].sort((a, b) => {
                const A = groups.get(a), B = groups.get(b);
                const ai = CATEGORY_ORDER.indexOf(A.cat);
                const bi = CATEGORY_ORDER.indexOf(B.cat);
                const aci = ai === -1 ? 999 : ai;
                const bci = bi === -1 ? 999 : bi;
                if (aci !== bci) return aci - bci;
                const subsList = PRODUCT_SUBCATEGORIES[A.cat] || [];
                const asi = A.sub ? subsList.indexOf(A.sub) : -1;
                const bsi = B.sub ? subsList.indexOf(B.sub) : -1;
                return asi - bsi;
              });

              const nodes = [];
              for (const key of sortedKeys) {
                const g = groups.get(key);
                nodes.push(
                  <tr key={`group-${key}`} className="bg-zinc-50/80" data-testid={`product-group-${key}`}>
                    <td colSpan={isAdmin ? 8 : 7} className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-[#1B2D5C]">
                      {g.label} <span className="text-zinc-400 font-mono ml-1">({g.rows.length})</span>
                    </td>
                  </tr>
                );
                for (const p of g.rows) {
                  const sup = suppliers.find((s) => s.id === p.supplier_id);
                  nodes.push(
                    <tr key={p.id} className="hover:bg-zinc-50" data-testid={`product-row-${p.id}`}>
                      <td className="px-4 py-2">
                        {p.image_url ? (
                          <div className="w-10 h-10 rounded bg-cover bg-center border border-zinc-200" style={{ backgroundImage: `url(${p.image_url})` }} />
                        ) : (
                          <div className="w-10 h-10 rounded bg-zinc-100 flex items-center justify-center text-zinc-400"><ImageIcon size={14} /></div>
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium">{p.name}</td>
                      <td className="px-4 py-3 text-zinc-500 font-mono text-xs">{p.barcode || "—"}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex text-[11px] font-medium uppercase tracking-wider px-2 py-0.5 bg-zinc-100 text-zinc-700 rounded">
                          {p.subcategory ? `${p.category} › ${p.subcategory}` : (p.category || "—")}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-zinc-600">{sup?.name || "—"}</td>
                      <td className="px-4 py-3 text-zinc-600">{p.unit}</td>
                      <td className="px-4 py-3 text-right font-mono font-semibold">{formatEUR(p.price)}</td>
                      {isAdmin && (
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => openEdit(p)} className="text-zinc-500 hover:text-[#1B2D5C] p-1" data-testid={`edit-product-${p.id}`} title="Bewerken">
                              <Pencil size={15} />
                            </button>
                            <button onClick={() => del(p.id)} className={`p-1 ${confirmDeleteId === p.id ? "text-white bg-red-600 rounded hover:bg-red-700" : "text-zinc-400 hover:text-red-600"}`} data-testid={`delete-product-${p.id}`} title={confirmDeleteId === p.id ? "Klik nogmaals om te bevestigen" : "Verwijderen"}>
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                }
              }
              if (nodes.length === 0) {
                nodes.push(
                  <tr key="empty"><td colSpan={isAdmin ? 8 : 7} className="py-10 text-center text-zinc-500">Geen producten.</td></tr>
                );
              }
              return nodes;
            })()}
          </tbody>
        </table>
      </div>

      {showForm && (
        <Modal onClose={() => { setShowForm(false); setEditingId(null); }} title={editingId ? "Product bewerken" : "Nieuw product"}>
          <form onSubmit={submit} className="space-y-3" data-testid="product-form">
            <Field label="Foto">
              <div className="flex items-center gap-3">
                {form.image_url ? (
                  <div className="w-16 h-16 rounded bg-cover bg-center border border-zinc-200 shrink-0" style={{ backgroundImage: `url(${form.image_url})` }} />
                ) : (
                  <div className="w-16 h-16 rounded bg-zinc-100 flex items-center justify-center text-zinc-400 shrink-0"><ImageIcon size={20} /></div>
                )}
                <label className="flex-1 inline-flex items-center justify-center gap-2 h-10 px-3 border border-zinc-300 hover:border-[#5BC044] rounded-md text-sm cursor-pointer text-zinc-600">
                  <Upload size={14} /> Foto kiezen
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="product-image-upload" />
                </label>
                {form.image_url && (
                  <button type="button" onClick={() => setForm({ ...form, image_url: "" })} className="text-zinc-400 hover:text-red-600 p-1">
                    <X size={16} />
                  </button>
                )}
              </div>
            </Field>
            <Field label="Naam"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="form-input" data-testid="product-name" /></Field>
            <Field label="Barcode (optioneel)"><input value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} className="form-input font-mono" placeholder="EAN/UPC code" data-testid="product-barcode" /></Field>
            <Field label="Categorie">
              <select required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value, subcategory: "" })} className="form-input" data-testid="product-category">
                {PRODUCT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            {PRODUCT_SUBCATEGORIES[form.category] && (
              <Field label="Subcategorie">
                <select required value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} className="form-input" data-testid="product-subcategory">
                  <option value="">— Kies —</option>
                  {PRODUCT_SUBCATEGORIES[form.category].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
            )}
            <Field label="Leverancier (optioneel)">
              <select value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })} className="form-input" data-testid="product-supplier">
                <option value="">—</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Eenheid"><input required value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="form-input" data-testid="product-unit" /></Field>
              <Field label="Prijs (€)"><input type="number" step="0.01" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="form-input" data-testid="product-price" /></Field>
            </div>
            <button type="submit" className="w-full h-10 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded-md text-sm font-semibold" data-testid="product-submit">
              {editingId ? "Opslaan" : "Toevoegen"}
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="overline block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

export function Modal({ children, title, onClose }) {
  return (
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="modal-overlay">
      <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl my-8" onClick={(e) => e.stopPropagation()} data-testid="modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold tracking-tight text-[#1B2D5C]">{title}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900 p-1" data-testid="modal-close"><X size={18} /></button>
        </div>
        {children}
      </div>
      <style>{`.form-input { width: 100%; height: 40px; padding: 0 12px; border: 1px solid #d4d4d8; border-radius: 6px; font-size: 14px; background: white; } .form-input:focus { outline: none; border-color: #5BC044; box-shadow: 0 0 0 3px rgba(91,192,68,0.18); }`}</style>
    </div>
  );
}
